import { navigate } from 'wouter/use-browser-location';
import { addToast, Dropdown, DropdownItem } from '@platform/components';
import { RepProjectPanel } from '@platform/client/schemas';
import { DeleteProjectModalContext } from '../Modals/DeleteProjectModal';
import { useContext } from 'react';
import { MyProjects, Projects } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';

interface ProjectActionDropdownProps {
  panelData: RepProjectPanel;
}

export const ProjectActionDropdown = (props: ProjectActionDropdownProps) => {
  const { panelData } = { ...props };
  const { openDeleteProjectModal } = useContext(DeleteProjectModalContext);

  const { project } = { ...panelData };
  const { id: projectId, publishingStage } = { ...project };

  const isProjectEditable = publishingStage === 'unpublished' || publishingStage === 'draft';
  const isProjectUnderReview =
    publishingStage === 'review' || publishingStage === 'revision_requested';

  const projectIsDeletable =
    !panelData.actionRequired.length &&
    !panelData.inProgress.length &&
    !panelData.inactive.length &&
    (isProjectEditable || isProjectUnderReview);

  const queryClient = useQueryClient();

  const requestReviewMutator = Projects.useRequestReview({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey(),
        });
        addToast({
          description: 'Project successfully submitted for review.',
        });
      },
    },
  });

  const unpublishProjectMutator = Projects.useUnpublishProject({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey(),
        });
        addToast({
          description: 'Project successfully unpublished. It has been moved to Inactive projects.',
        });
      },
    },
  });

  const cloneProjectMutator = Projects.useCloneProject({
    mutation: {
      onSuccess: (data: { project_id: number }) => {
        queryClient.invalidateQueries({
          queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey(),
        });
        navigate(`/platform/dashboard/edit-project/${data.project_id}`);
      },
    },
  });

  if (!projectId) {
    return null;
  }

  return (
    <Dropdown type={'icon'} ariaLabel='Dropdown button' iconName='dots-vertical'>
      {!isProjectEditable && (
        <DropdownItem
          onAction={() => {
            cloneProjectMutator.mutate({
              projectId,
            });
          }}
        >
          Clone
        </DropdownItem>
      )}
      {(isProjectEditable || isProjectUnderReview) && (
        <DropdownItem href={`/platform/dashboard/edit-project/${projectId}`}>Edit</DropdownItem>
      )}
      {!isProjectEditable && !panelData.completed && (
        <DropdownItem
          onAction={() => {
            unpublishProjectMutator.mutate({
              projectId,
            });
          }}
        >
          Unpublish
        </DropdownItem>
      )}
      {(isProjectEditable || isProjectUnderReview) && (
        <DropdownItem
          onAction={() => {
            requestReviewMutator.mutate({
              data: { projectId },
            });
          }}
        >
          Publish
        </DropdownItem>
      )}
      {projectIsDeletable && (
        <DropdownItem
          onAction={() => {
            openDeleteProjectModal(projectId);
          }}
        >
          Delete
        </DropdownItem>
      )}
    </Dropdown>
  );
};
